import PropTypes from 'prop-types'
import React from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import Price from '@/checkout-instore-legacy/components/Commons/Price'
import IconArrowRight from '@/checkout-instore-legacy/components/Commons/Icons/ArrowRight'
import { ANONYMOUS_NAME } from '@/checkout-instore-legacy/constants/IdentificationConstants'
import { isInStoreAnonymousEmail } from '@/checkout-instore-legacy/utils/EmailUtils'
import { formatSimpleDate } from '@/checkout-instore-legacy/utils/DateUtils'
import {
  getTotalItems,
  getDocumentType,
} from '@/checkout-instore-legacy/utils/orderFormUtils'
import { maskDocument } from '@/checkout-instore-legacy/utils/DocumentUtils'

function getUserId(order, intl) {
  const { clientProfileData, clientName } = order

  if (clientName != null) {
    if (clientName.trim() && clientName.trim() !== ANONYMOUS_NAME) {
      return clientName
    }
  } else if (clientProfileData) {
    const { firstName, lastName } = clientProfileData

    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }

    const { email } = clientProfileData

    if (email && !isInStoreAnonymousEmail(email)) {
      return email
    }

    const { document: documentNumber } = clientProfileData

    if (documentNumber) {
      const documentType = getDocumentType(order)
      return maskDocument({
        documentNumber,
        documentType,
      })
    }
  }

  return intl.formatMessage({
    id: 'anonymousLabel',
  })
}

const Order = ({
  order,
  orderId,
  showOrderId,
  editMode,
  isSelected,
  intl,
  onRecoverOrder,
  onRemoveOrder,
  onCopyOrder,
}) => {
  const tablet = window.innerWidth > 600
  const id = orderId || order.orderFormId
  const idToShow = showOrderId ? `${id} | ` : ''
  const { date, paymentsPaid } = order
  const paidValue =
    paymentsPaid && paymentsPaid.length > 0
      ? paymentsPaid
          .map((paymentPaid) => paymentPaid.payment.value)
          .reduce((acc, cur) => acc + cur, 0)
      : 0
  const itemQuantity = getTotalItems(order)

  return (
    <div
      id={`order-${id}`}
      className={`order-list-item pointer hover-bg-light-silver bg-animate flex pl4 pl5-ns bb b--black-20 ${
        isSelected ? 'bg-light-silver' : ''
      }`}
      onClick={() => onRecoverOrder(id)}
    >
      <div className="flex-auto pv5 pv6-ns pl4 pl5-ns noselect">
        <div className="mb4 mb5-ns f5 fw7 f3-ns">
          {`${idToShow}${getUserId(order, intl)}`}
          {order.vendorName ? (
            <span className="db f6 fw3 pt3 f4-ns pt4-ns">
              <FormattedMessage id="orderVendorLabel" />
              {': '}
              {order.vendorName}
            </span>
          ) : null}
        </div>
        <div className="gray fw3 f6 f4-ns">
          {order.value > 0 && (
            <span>
              <Price value={order.value} />
              {paidValue > 0 && (
                <React.Fragment>
                  <span>{' - '}</span>
                  <span className="green b">
                    <Price value={paidValue} /> <FormattedMessage id="paid" />
                  </span>
                </React.Fragment>
              )}
              <span className="order-data-sep ml4 mr4">{'|'}</span>
            </span>
          )}
          {!!itemQuantity && (
            <span className="order-quantity">
              <FormattedMessage
                id="orderQuantityOfItems"
                values={{ numberOfItems: itemQuantity }}
              />
            </span>
          )}
          {date ? (
            <span>
              <span className="order-data-sep">{'  |  '}</span>
              <span>{formatSimpleDate(new Date(date))}</span>
            </span>
          ) : null}
        </div>
      </div>
      {editMode ? (
        <div className="flex">
          {onCopyOrder ? (
            <div
              className="flex-none self-center f3 f2-ns ph4 ph5-ns ph6-l"
              onClick={() => onCopyOrder && onCopyOrder(id)}
            >
              <i className="c-action-primary fa fa-docs" />
            </div>
          ) : null}
          <div
            className="flex-none self-center f3 f2-ns ph4 ph5-ns ph6-l"
            onClick={() => onRemoveOrder && onRemoveOrder(id)}
          >
            <i className="c-danger fa fa-cancel" />
          </div>
        </div>
      ) : (
        <div className="flex-none self-center pr5 pr6-ns">
          <IconArrowRight
            width={tablet ? 28 : 16}
            height={tablet ? 28 : 16}
            color={'#DDDDDD'}
          />
        </div>
      )}
    </div>
  )
}

Order.defaultProps = {
  showOrderId: false,
}

Order.propTypes = {
  order: PropTypes.object.isRequired,
  orderId: PropTypes.string,
  showOrderId: PropTypes.bool.isRequired,
  editMode: PropTypes.bool,
  isSelected: PropTypes.bool.isRequired,
  onCopyOrder: PropTypes.func,
  onRemoveOrder: PropTypes.func,
  onRecoverOrder: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
}

export default injectIntl(Order)
